import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calculator, CheckCircle, Users, TrendingUp, FileText, Shield } from 'lucide-react';

const Accountants = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What should accountants include in their performance review?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Include close cycle improvements, audit results, cost savings identified, process automation wins, compliance achievements, and error reduction rates. Quantify impact: 'Reduced monthly close from 12 days to 7 days while maintaining zero material adjustments.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do finance professionals demonstrate value beyond accurate numbers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document process improvements, system implementations, cost savings identified, strategic analysis provided to leadership, and cross-functional collaboration. Show how your financial insights drove business decisions, not just that you balanced the books."
        }
      },
      {
        "@type": "Question",
        "name": "What metrics matter for accounting performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key metrics include: close cycle time, audit findings, error rates, process efficiency gains, cost savings identified, report accuracy, and deadline adherence. Also track certifications earned, systems implemented, and strategic projects supported."
        }
      },
      {
        "@type": "Question",
        "name": "How often should accountants document their achievements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document monthly around close cycles, capturing process improvements, audit wins, and strategic contributions as they happen. Track positive feedback from auditors, stakeholders, and leadership immediately. Use a tool like BragBox to capture achievements in real-time."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Accountants & Finance Professionals | BragBox</title>
        <meta name="description" content="Ace your accounting performance review with actionable tips. Learn how to showcase process improvements, audit success, and strategic financial contributions." />
        <meta name="keywords" content="accountant performance review, finance self-evaluation, CPA review tips, accounting metrics, finance career growth, controller review" />
        <link rel="canonical" href="https://bragbox.ai/tips/accountants" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <Calculator className="w-4 h-4" />
              Accounting & Finance Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Accountants & Finance Professionals
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              You keep the numbers right and the company compliant. Learn how to document process improvements, audit wins, and the strategic value you bring beyond accurate financial statements.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Accountants Struggle with Self-Evaluations</h2>
            <p className="text-gray-300 mb-6">
              You shaved 5 days off the monthly close through process improvements. You caught an error that would have triggered a restatement. You provided the analysis that helped leadership make a $2M investment decision. But when review time comes, people just assume the books balance because that's your job.
            </p>
            <p className="text-gray-300">
              Accounting work is often invisible precisely because accurate numbers are expected. Nobody celebrates a clean audit; they celebrate when sales hits quota. Your best work often prevents problems that nobody ever knows about.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Accounting Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Quantify Close Cycle Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Faster closes give leadership better visibility. Document cycle time reductions and the process changes that enabled them.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Reduced monthly close from 12 business days to 7 business days through automated reconciliations and parallel processing. Year-end close completed 3 days ahead of schedule, providing earlier visibility for board reporting."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Audit Success</h3>
                    <p className="text-gray-300 mb-3">
                      Clean audits and minimal findings represent real value. Track audit results, findings trend, and your role in maintaining compliance.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Achieved clean audit opinion for 3rd consecutive year with zero material weaknesses. Reduced PBC list turnaround from 5 days to 2 days. Audit fees reduced 15% through improved documentation and process efficiency."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Track Error Prevention and Detection</h3>
                    <p className="text-gray-300 mb-3">
                      Errors caught before they become problems are achievements. Document controls you've strengthened and issues you've identified.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified revenue recognition error in Q2 that would have resulted in $340K overstatement. Implemented monthly control that has caught 8 similar errors before close. Overall adjustment rate decreased from 2.3% to 0.4% of revenue."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Highlight Cost Savings Identified</h3>
                    <p className="text-gray-300 mb-3">
                      Your analysis can find money. Document cost reduction opportunities you identified through financial review or variance analysis.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Identified $127K in duplicate vendor payments through reconciliation review. Expense analysis revealed $89K in unused software subscriptions, now cancelled. Cash management optimization increased interest income by $34K annually."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Process Automation</h3>
                    <p className="text-gray-300 mb-3">
                      Automation saves time and reduces errors. Track hours saved, error reduction, and new capabilities enabled through automation.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Automated bank reconciliation process, saving 15 hours monthly. Built Excel model for revenue allocation reducing manual calculation time by 80%. Implemented automated AP three-way match reducing processing time by 40%."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Track Strategic Analysis Provided</h3>
                    <p className="text-gray-300 mb-3">
                      Financial analysis that informs decisions shows business partnership. Document analyses you've provided and decisions they influenced.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Provided financial analysis for 4 capital investment decisions totaling $2.4M. Built pricing model that increased gross margin 3 points on new product line. Monthly variance analysis identified $180K in operational improvement opportunities."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Document Compliance Achievements</h3>
                    <p className="text-gray-300 mb-3">
                      Staying compliant avoids penalties and reputational damage. Track regulatory filings, tax compliance, and new standards implemented.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Filed all quarterly and annual reports on time with zero amendments required. Implemented ASC 842 lease accounting standard 3 months ahead of deadline. Sales tax compliance maintained across 12 states with zero audit findings."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Highlight System Implementations</h3>
                    <p className="text-gray-300 mb-3">
                      New systems and tools improve capabilities. Document implementations you led or contributed to and their benefits.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Led implementation of new expense management system, reducing reimbursement cycle from 14 days to 3 days. Configured ERP reporting module eliminating 20 hours of monthly manual report building. Trained 45 users on new systems with 92% satisfaction rating."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Capture Professional Development</h3>
                    <p className="text-gray-300 mb-3">
                      CPE hours, certifications, and new skills show commitment to growth. Document your professional development investments.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Completed 50 CPE hours including advanced Excel, Power BI certification, and ASC 606 revenue recognition. Earned CMA designation. Applied new data visualization skills to create executive dashboard now used in monthly board meetings."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Document Wins Throughout the Year</h3>
                    <p className="text-gray-300 mb-3">
                      Don't wait until review season to remember your achievements. Track wins monthly, especially around close cycles.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After each monthly close, take 5 minutes to log wins in BragBox: process improvements, errors caught, stakeholder feedback, time saved. After each quarter-end, document any strategic analysis or audit prep wins. Your annual review will practically write itself.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Accountants Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Close & Accuracy</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Close cycle time</li>
                  <li>• Adjustment rate</li>
                  <li>• Error detection rate</li>
                  <li>• Reconciliation completion</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Audit & Compliance</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Audit findings</li>
                  <li>• Filing deadline adherence</li>
                  <li>• Control effectiveness</li>
                  <li>• Compliance audit results</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Efficiency</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Hours saved through automation</li>
                  <li>• Process cycle time improvements</li>
                  <li>• Report turnaround time</li>
                  <li>• PBC list response time</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Business Impact</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Cost savings identified</li>
                  <li>• Strategic analyses provided</li>
                  <li>• Decisions influenced</li>
                  <li>• Stakeholder satisfaction</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What should accountants include in their performance review?</h3>
                <p className="text-gray-300">Include close cycle improvements, audit results, cost savings identified, process automation wins, compliance achievements, and error reduction rates. Quantify impact: "Reduced monthly close from 12 days to 7 days while maintaining zero material adjustments."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do finance professionals demonstrate value beyond accurate numbers?</h3>
                <p className="text-gray-300">Document process improvements, system implementations, cost savings identified, strategic analysis provided to leadership, and cross-functional collaboration. Show how your financial insights drove business decisions, not just that you balanced the books.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics matter for accounting performance reviews?</h3>
                <p className="text-gray-300">Key metrics include: close cycle time, audit findings, error rates, process efficiency gains, cost savings identified, report accuracy, and deadline adherence. Also track certifications earned, systems implemented, and strategic projects supported.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should accountants document their achievements?</h3>
                <p className="text-gray-300">Document monthly around close cycles, capturing process improvements, audit wins, and strategic contributions as they happen. Track positive feedback from auditors, stakeholders, and leadership immediately. Use a tool like BragBox to capture achievements in real-time.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Balance Your Achievements as Well as You Balance the Books</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps accountants and finance professionals track process wins, audit successes, and strategic contributions throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Accountants;
